import { parseIsoDateTime } from "@/lib/utils/datetime";

export const ORDER_TIME_ZONE =
  process.env.NEXT_PUBLIC_APP_TIMEZONE || "America/Los_Angeles";

const ORDER_DATE_TIME_FORMAT_OPTIONS = {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: ORDER_TIME_ZONE,
};

const ORDER_TIME_FORMAT_OPTIONS = {
  timeStyle: "short",
  timeZone: ORDER_TIME_ZONE,
};

const ORDER_ACTIVITY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: ORDER_TIME_ZONE,
});

const ORDER_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeZone: ORDER_TIME_ZONE,
});

const ORDER_DATE_KEY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: ORDER_TIME_ZONE,
});

const getOrderDateKey = (value) => {
  const parsed = parseIsoDateTime(value);
  if (!parsed) return "unknown";

  const parts = ORDER_DATE_KEY_FORMATTER.formatToParts(parsed);
  const part = (type) => parts.find((entry) => entry.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const getPreviousDateKey = (dateKey) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (![year, month, day].every(Number.isFinite)) return "unknown";

  const previous = new Date(Date.UTC(year, month - 1, day - 1));
  return previous.toISOString().slice(0, 10);
};

const formatOrderDateGroupLabel = (value, now) => {
  const parsed = parseIsoDateTime(value);
  if (!parsed) return "Unknown date";

  const todayKey = getOrderDateKey(now.toISOString());
  const valueKey = getOrderDateKey(value);
  if (valueKey === todayKey) return "Today";
  if (valueKey === getPreviousDateKey(todayKey)) return "Yesterday";

  return ORDER_DATE_FORMATTER.format(parsed);
};

export const ORDER_STATUS_LABELS = {
  pending_payment: "Pending payment",
  payment_failed: "Payment failed",
  awaiting_confirmation: "Confirmation required",
  declined: "Declined",
  expired: "Expired",
  requested: "Requested",
  confirmed: "Confirmed",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  fulfilled: "Fulfilled",
  cancelled: "Canceled",
  completed: "Completed",
};

export const ORDER_STATUS_DESCRIPTIONS = {
  pending_payment: "Complete Stripe Checkout to place the order.",
  payment_failed: "Payment did not complete. Try checkout again.",
  awaiting_confirmation: "The business still needs to accept this order.",
  declined: "The business declined this order before payment was captured.",
  expired: "The confirmation period expired before payment was captured.",
  requested: "We received the order request.",
  confirmed: "The vendor confirmed the order.",
  ready: "Your order is ready for pickup.",
  out_for_delivery: "Your order is on the way.",
  fulfilled: "Order completed.",
  cancelled: "Order canceled.",
  completed: "Order completed.",
};

export const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Order times are formatted as en-US, so the day period is AM/PM. Intl puts a
// breakable space (or U+202F in some runtimes) before it, which lets narrow rows
// wrap "PM" onto its own line. Swap only that separator for a no-break space;
// the date and any other spaces can still wrap. Use for display text only.
export const keepDayPeriodWithTime = (value) =>
  typeof value === "string" ? value.replace(/(\d)\s+(AM|PM)\b/g, "$1 $2") : value;

export const formatOrderDateTime = (value) => {
  const parsed = parseIsoDateTime(value);
  if (!parsed) return "—";

  return keepDayPeriodWithTime(
    parsed.toLocaleString("en-US", ORDER_DATE_TIME_FORMAT_OPTIONS)
  );
};

export const formatOrderActivityTime = (value) => {
  const parsed = parseIsoDateTime(value);
  if (!parsed) return "";

  const parts = ORDER_ACTIVITY_FORMATTER.formatToParts(parsed);
  const part = (type) => parts.find((entry) => entry.type === type)?.value || "";
  const dayPeriod = part("dayPeriod");

  return keepDayPeriodWithTime(
    `${part("month")} ${part("day")} at ${part("hour")}:${part("minute")}${
      dayPeriod ? ` ${dayPeriod}` : ""
    }`
  );
};

export const getOrderStatusTimestamp = (order) => {
  if (!order) return null;

  switch (order.status) {
    case "pending_payment":
      return order.created_at || null;
    case "awaiting_confirmation":
      return order.payment_authorized_at || order.created_at || null;
    case "requested":
      return order.paid_at || order.created_at || null;
    case "confirmed":
      return order.confirmed_at || null;
    case "ready":
      return order.ready_at || null;
    case "out_for_delivery":
      return order.out_for_delivery_at || null;
    case "fulfilled":
    case "completed":
      return order.fulfilled_at || null;
    case "cancelled":
      return order.cancelled_at || null;
    case "declined":
    case "expired":
    case "payment_failed":
      return order.payment_cancelled_at || order.cancelled_at || null;
    default:
      return null;
  }
};

export const getOrderPurchaseTimestamp = (order) =>
  order?.paid_at || order?.created_at || null;

export const formatOrderPurchaseDateTime = (order) =>
  formatOrderDateTime(getOrderPurchaseTimestamp(order));

export const formatOrderPurchaseTime = (order) => {
  const parsed = parseIsoDateTime(getOrderPurchaseTimestamp(order));
  if (!parsed) return "—";

  return keepDayPeriodWithTime(
    parsed.toLocaleTimeString("en-US", ORDER_TIME_FORMAT_OPTIONS)
  );
};

export const compareOrdersByPurchaseRecency = (left, right) => {
  const leftTime = parseIsoDateTime(getOrderPurchaseTimestamp(left))?.getTime() ?? 0;
  const rightTime = parseIsoDateTime(getOrderPurchaseTimestamp(right))?.getTime() ?? 0;

  if (rightTime !== leftTime) return rightTime - leftTime;

  return String(right?.id || "").localeCompare(String(left?.id || ""));
};

export const sortOrdersByPurchaseRecency = (orders = []) =>
  [...orders].sort(compareOrdersByPurchaseRecency);

// Pass { sort: false } for rows already in server page order; re-sorting a
// paginated page client-side could disagree with the database's page boundary.
export const groupOrdersByPurchaseDate = (
  orders = [],
  now = new Date(),
  { sort = true } = {}
) => {
  const groups = [];
  const groupIndexByDate = new Map();

  (sort ? sortOrdersByPurchaseRecency(orders) : orders).forEach((order) => {
    const timestamp = getOrderPurchaseTimestamp(order);
    const dateKey = getOrderDateKey(timestamp);
    let group = groupIndexByDate.get(dateKey);

    if (!group) {
      group = {
        key: dateKey,
        label: formatOrderDateGroupLabel(timestamp, now),
        orders: [],
      };
      groupIndexByDate.set(dateKey, group);
      groups.push(group);
    }

    group.orders.push(order);
  });

  return groups;
};

export const getOrderStatusLabel = (status) =>
  ORDER_STATUS_LABELS[status] || status || "Unknown";

export const getOrderStatusDescription = (status) =>
  ORDER_STATUS_DESCRIPTIONS[status] || "Order in progress.";
